import { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Clock, Calendar, User, ChevronRight, Video, Users, Filter, BookOpen, Search, X, RotateCcw, Loader2 } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

import { API, AuthContext } from '../App';

function AllLiveClasses() {
  const { user } = useContext(AuthContext);
  const [reschedulingId, setReschedulingId] = useState(null);

  const handleRescheduleTomorrow = async (e, liveClass) => {
    e.preventDefault();
    e.stopPropagation();
    if (reschedulingId) return;
    setReschedulingId(liveClass.id);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/live-classes/${liveClass.id}/reschedule`,
        { preset: 'tomorrow_same_time' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Rescheduled to ${res.data.new_date} at ${res.data.new_time}`);
      setLiveClasses(prev => prev.map(c => c.id === liveClass.id
        ? { ...c, status: 'upcoming', date: res.data.new_date, time: res.data.new_time }
        : c));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not reschedule. Try again.');
    } finally {
      setReschedulingId(null);
    }
  };
  const [liveClasses, setLiveClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const subjects = ['Mathematics', 'Physics', 'Chemistry', 'Biology', 'English', 'History', 'Geography', 'Kiswahili'];
  const levels = ['Form I', 'Form II', 'Form III', 'Form IV', 'O-Level', 'A-Level'];
  const statuses = ['live', 'upcoming', 'completed', 'to_be_rescheduled'];

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/live-classes?limit=50`),
      axios.get(`${API}/teachers`)
    ]).then(([classesRes, teachersRes]) => {
      setLiveClasses(classesRes.data);
      setTeachers(teachersRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const toggleFilter = (value, filterArray, setFilterArray) => {
    if (filterArray.includes(value)) {
      setFilterArray(filterArray.filter(item => item !== value));
    } else {
      setFilterArray([...filterArray, value]);
    }
  };

  const filteredClasses = liveClasses.filter(liveClass => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!liveClass.title?.toLowerCase().includes(q) && !liveClass.description?.toLowerCase().includes(q) && !liveClass.teacher_name?.toLowerCase().includes(q) && !liveClass.subject?.toLowerCase().includes(q)) return false;
    }
    if (selectedSubjects.length > 0 && !selectedSubjects.includes(liveClass.subject)) return false;
    if (selectedLevels.length > 0 && !selectedLevels.includes(liveClass.level)) return false;
    if (selectedTeachers.length > 0 && !selectedTeachers.includes(liveClass.teacher_id)) return false;
    if (selectedStatus.length > 0 && !selectedStatus.includes(liveClass.status)) return false;
    return true;
  });

  const clearFilters = () => {
    setSelectedSubjects([]);
    setSelectedLevels([]);
    setSelectedTeachers([]);
    setSelectedStatus([]);
    setSearchQuery('');
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-4">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Link to="/" className="hover:text-teal-600">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-900 font-semibold">All Live Classes</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-12">
        {/* Search Bar */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search live classes by title, subject, or teacher..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-xl bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            data-testid="live-classes-search"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Mobile Filter Toggle */}
        <div className="lg:hidden mb-4 flex items-center justify-between">
          <Button variant="outline" onClick={() => setShowMobileFilters(!showMobileFilters)}
            className="gap-2 border-slate-200 text-slate-600 hover:bg-slate-50" data-testid="mobile-filter-toggle">
            <Filter className="w-4 h-4" /> Filters
            {(selectedSubjects.length + selectedLevels.length + selectedStatus.length) > 0 && (
              <span className="bg-teal-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                {selectedSubjects.length + selectedLevels.length + selectedStatus.length}
              </span>
            )}
          </Button>
          <span className="text-sm text-slate-500">{filteredClasses.length} results</span>
        </div>

        {/* Mobile Filters Panel */}
        {showMobileFilters && (
          <div className="lg:hidden bg-white rounded-xl border border-slate-200 p-4 mb-6" data-testid="mobile-filters-panel">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Subject</label>
                <select onChange={(e) => e.target.value ? setSelectedSubjects([e.target.value]) : setSelectedSubjects([])} value={selectedSubjects[0] || ''}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
                  <option value="">All Subjects</option>
                  {subjects.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Level</label>
                <select onChange={(e) => e.target.value ? setSelectedLevels([e.target.value]) : setSelectedLevels([])} value={selectedLevels[0] || ''}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
                  <option value="">All Levels</option>
                  {levels.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Status</label>
                <select onChange={(e) => e.target.value ? setSelectedStatus([e.target.value]) : setSelectedStatus([])} value={selectedStatus[0] || ''}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
                  <option value="">All Statuses</option>
                  {statuses.map(s => <option key={s} value={s} className="capitalize">{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                </select>
              </div>
              <div className="flex items-end">
                <Button variant="ghost" size="sm" onClick={() => { clearFilters(); setShowMobileFilters(false); }} className="text-xs">Clear All</Button>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-8">
          <aside className="w-72 flex-shrink-0 hidden lg:block">
            <div className="bg-white rounded-xl p-6 border border-slate-200 sticky top-24">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold flex items-center gap-2"><Filter className="w-5 h-5" />Filters</h3>
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs">Clear All</Button>
              </div>

              <div className="mb-6 pb-6 border-b border-slate-200">
                <h4 className="font-bold text-sm mb-3">Status</h4>
                <div className="space-y-2">
                  {statuses.map(status => (
                    <label key={status} className="flex items-center gap-2 cursor-pointer hover:text-teal-600">
                      <Checkbox checked={selectedStatus.includes(status)} onCheckedChange={() => toggleFilter(status, selectedStatus, setSelectedStatus)} />
                      <span className="text-sm capitalize">{status}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-6 pb-6 border-b border-slate-200">
                <h4 className="font-bold text-sm mb-3">Subject</h4>
                <div className="space-y-2">
                  {subjects.map(subject => (
                    <label key={subject} className="flex items-center gap-2 cursor-pointer hover:text-teal-600">
                      <Checkbox checked={selectedSubjects.includes(subject)} onCheckedChange={() => toggleFilter(subject, selectedSubjects, setSelectedSubjects)} />
                      <span className="text-sm">{subject}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-6 pb-6 border-b border-slate-200">
                <h4 className="font-bold text-sm mb-3">Level</h4>
                <div className="space-y-2">
                  {levels.map(level => (
                    <label key={level} className="flex items-center gap-2 cursor-pointer hover:text-teal-600">
                      <Checkbox checked={selectedLevels.includes(level)} onCheckedChange={() => toggleFilter(level, selectedLevels, setSelectedLevels)} />
                      <span className="text-sm">{level}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-sm mb-3">Teacher</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {teachers.map(teacher => (
                    <label key={teacher.id} className="flex items-center gap-2 cursor-pointer hover:text-teal-600">
                      <Checkbox checked={selectedTeachers.includes(teacher.id)} onCheckedChange={() => toggleFilter(teacher.id, selectedTeachers, setSelectedTeachers)} />
                      <span className="text-sm">{teacher.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          <main className="flex-1">
            <div className="mb-6">
              <h1 className="text-3xl md:text-4xl font-black mb-2">All Live Classes</h1>
              <p className="text-slate-600">Showing {filteredClasses.length} of {liveClasses.length} live classes</p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
              {filteredClasses.map(liveClass => (
                <Link key={liveClass.id} to={`/live-classes/${liveClass.id}`} className="group bg-white rounded-xl sm:rounded-2xl overflow-hidden border border-slate-200 hover:shadow-2xl hover:shadow-slate-200/60 transition-all duration-300 hover:-translate-y-1" data-testid={`live-class-card-${liveClass.id}`}>
                  {/* Mobile compact card */}
                  <div className="sm:hidden">
                    <div className="relative aspect-[3/4] overflow-hidden">
                      <img src={liveClass.thumbnail} alt={liveClass.title} className="w-full h-full object-cover" loading="lazy" />
                      {(liveClass.status === 'ongoing' || liveClass.status === 'live' || liveClass.is_live) && (
                        <span className="absolute top-1 left-1 bg-red-500 text-white text-[7px] px-1 py-0.5 rounded font-bold flex items-center gap-0.5">
                          <span className="w-1 h-1 bg-white rounded-full animate-pulse" />LIVE
                        </span>
                      )}
                      {liveClass.status === 'upcoming' && <span className="absolute top-1 left-1 bg-blue-500 text-white text-[7px] px-1 py-0.5 rounded font-bold">UPCOMING</span>}
                    </div>
                    <div className="p-1.5">
                      <h3 className="text-[11px] font-semibold line-clamp-2 text-slate-800 leading-tight">{liveClass.title}</h3>
                      <p className="text-[9px] text-slate-500 mt-0.5 line-clamp-1">{liveClass.teacher_name || 'Instructor'}</p>
                    </div>
                  </div>

                  {/* Desktop detailed card */}
                  <div className="hidden sm:block">
                    <div className="relative h-48 overflow-hidden">
                      <img src={liveClass.thumbnail} alt={liveClass.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                      <div className="absolute top-3 left-3">
                        {(liveClass.status === 'ongoing' || liveClass.status === 'live' || liveClass.is_live) && (
                          <div className="flex items-center gap-2 bg-red-500 text-white px-3 py-1.5 rounded-full shadow-lg">
                            <div className="relative flex items-center"><div className="w-2 h-2 bg-white rounded-full animate-pulse"></div></div>
                            <Video className="w-3.5 h-3.5" /><span className="text-xs font-bold">LIVE NOW</span>
                          </div>
                        )}
                        {liveClass.status === 'upcoming' && <div className="flex items-center gap-1.5 bg-blue-500 text-white px-3 py-1.5 rounded-full shadow-lg"><Calendar className="w-3.5 h-3.5" /><span className="text-xs font-bold">UPCOMING</span></div>}
                        {liveClass.status === 'scheduled' && <div className="flex items-center gap-1.5 bg-blue-500 text-white px-3 py-1.5 rounded-full shadow-lg"><Calendar className="w-3.5 h-3.5" /><span className="text-xs font-bold">SCHEDULED</span></div>}
                        {liveClass.status === 'completed' && <div className="flex items-center gap-1.5 bg-slate-500 text-white px-3 py-1.5 rounded-full shadow-lg"><span className="text-xs font-bold">COMPLETED</span></div>}
                        {liveClass.status === 'to_be_rescheduled' && <div className="flex items-center gap-1.5 bg-amber-500 text-white px-3 py-1.5 rounded-full shadow-lg"><Calendar className="w-3.5 h-3.5" /><span className="text-xs font-bold">TO BE RESCHEDULED</span></div>}
                      </div>
                      <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2">
                        <span className="bg-white/95 backdrop-blur-sm text-indigo-700 px-3 py-1.5 rounded-full text-xs font-bold shadow-lg">{liveClass.level}</span>
                        <span className="bg-white/95 backdrop-blur-sm text-teal-700 px-3 py-1.5 rounded-full text-xs font-bold shadow-lg">{liveClass.subject}</span>
                      </div>
                      <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm text-slate-700 px-3 py-1.5 rounded-full text-xs font-bold shadow-lg flex items-center gap-1"><Users className="w-3.5 h-3.5" />{liveClass.students_attending || 0}</div>
                    </div>
                    <div className="p-5">
                      <h3 className="text-lg font-bold mb-2 line-clamp-2 group-hover:text-teal-600 transition-colors">{liveClass.title}</h3>
                      <p className="text-sm text-slate-600 mb-2 flex items-center gap-1"><User className="w-3.5 h-3.5" />by {liveClass.teacher_name || 'Instructor'}</p>
                      <div className="flex items-center justify-between text-sm mb-3 pb-3 border-b border-slate-100">
                        <div className="flex items-center gap-1 text-slate-500"><BookOpen className="w-4 h-4" /><span className="font-semibold text-slate-700">Session {liveClass.current_session || 1}/{liveClass.total_sessions || 1}</span></div>
                        <div className="flex items-center gap-1 text-slate-500"><Clock className="w-4 h-4" />{liveClass.duration || 60}min</div>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-600 mb-4">
                        <div className="flex items-center gap-1"><Calendar className="w-4 h-4 text-teal-600" />{liveClass.date ? new Date(liveClass.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'TBD'}</div>
                        <div className="flex items-center gap-1"><Clock className="w-4 h-4 text-teal-600" />{liveClass.time || 'TBD'}</div>
                      </div>
                    <div className="flex justify-between items-center">
                      <span className="text-2xl font-black text-teal-600">{(liveClass.price || 0) === 0 ? 'Free' : `TSh ${(liveClass.price || 0).toLocaleString()}`}</span>
                      {liveClass.status === 'to_be_rescheduled' && user && (user.id === liveClass.teacher_id || user.role === 'admin') ? (
                        <Button
                          size="sm"
                          className="rounded-full bg-amber-500 hover:bg-amber-600 text-white"
                          disabled={reschedulingId === liveClass.id}
                          data-testid={`reschedule-tomorrow-${liveClass.id}`}
                          onClick={(e) => handleRescheduleTomorrow(e, liveClass)}
                        >
                          {reschedulingId === liveClass.id
                            ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Rescheduling…</>
                            : <><RotateCcw className="w-4 h-4 mr-1.5" />Reschedule for tomorrow</>}
                        </Button>
                      ) : (
                        <Button size="sm" className={`rounded-full ${(liveClass.status === 'ongoing' || liveClass.status === 'live' || liveClass.is_live) ? 'bg-red-500 hover:bg-red-600 animate-pulse' : ''}`} disabled={liveClass.status === 'to_be_rescheduled'}>
                          {(liveClass.status === 'ongoing' || liveClass.status === 'live' || liveClass.is_live) ? 'Join Now' :
                           liveClass.status === 'to_be_rescheduled' ? 'Awaiting reschedule' :
                           (liveClass.status === 'upcoming' || liveClass.status === 'scheduled') ? 'Book Seat' :
                           'View Recording'}
                        </Button>
                      )}
                    </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {filteredClasses.length === 0 && (
              <div className="text-center py-12">
                <p className="text-slate-500">No live classes match your filters.</p>
                <Button onClick={clearFilters} className="mt-4">Clear Filters</Button>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

export default AllLiveClasses;